/**
 * Transaction utility functions
 * Shared utilities for transaction sorting, filtering, and processing
 */

import { Transaction } from '@/services/platformApi';

/**
 * Sort transactions chronologically (newest first)
 */
export const sortTransactionsChronologically = (transactions: Transaction[]): Transaction[] => {
  return [...transactions].sort((a, b) => {
    try {
      const dateA = new Date(a.updatedAt || a.createdAt || 0);
      const dateB = new Date(b.updatedAt || b.createdAt || 0);
      return dateB.getTime() - dateA.getTime(); // Most recent first
    } catch (error) {
      console.warn('Error sorting transactions by date:', error, { a, b });
      return 0;
    }
  });
};

/**
 * Deduplicate transactions by key/id
 */
export const deduplicateTransactions = (transactions: Transaction[]): Transaction[] => {
  if (!Array.isArray(transactions)) {
    console.warn('🚨 deduplicateTransactions received non-array:', transactions);
    return [];
  }
  
  const seen = new Set();
  const deduplicated: Transaction[] = [];
  let duplicateCount = 0;
  let malformedCount = 0;
  
  for (const transaction of transactions) {
    try {
      if (!transaction) {
        malformedCount++;
        continue;
      }
      
      const key = transaction.id || transaction.key || transaction._id;
      if (!key) {
        console.warn('🚨 Transaction missing ID/key:', transaction);
        malformedCount++;
        continue;
      }
      
      if (!seen.has(key)) {
        seen.add(key);
        deduplicated.push(transaction);
      } else {
        duplicateCount++;
      }
    } catch (error) {
      console.error('🚨 Error processing transaction:', error, transaction);
      malformedCount++;
    }
  }
  
  if (duplicateCount > 0) {
    console.warn(`🔄 Removed ${duplicateCount} duplicate transactions from dataset`);
  }
  if (malformedCount > 0) {
    console.warn(`🚨 Filtered out ${malformedCount} malformed transactions`);
  }
  
  return deduplicated;
};

/**
 * Validate transaction has required fields
 */
export const isValidTransaction = (tx: any): tx is Transaction => {
  if (!tx) return false;
  // Must have either key, id, or _id
  if (!tx.key && !tx.id && !tx._id) return false;
  // Must have basic required fields
  if (!tx.amount && tx.amount !== 0) return false;
  if (!tx.currency) return false;
  return true;
};

/**
 * Determine if separate sections should be shown
 */
export const shouldShowSeparateSections = (
  sortMode: 'chronological' | 'grouped',
  pendingTransactions: Transaction[],
  completedTransactions: Transaction[]
): boolean => {
  return sortMode === 'grouped' && (pendingTransactions.length > 0 || completedTransactions.length > 0);
};

/**
 * Check if a transaction is a balance operation (topup or transfer out)
 */
export const isBalanceOperation = (transaction: Transaction): boolean => {
  const tokenType = transaction.tokenType as string;
  return tokenType === 'balance_up' || tokenType === 'balance_down';
};

/**
 * Filter balance operations from transactions
 */
export const filterBalanceOperations = (transactions: Transaction[]): Transaction[] => {
  return transactions.filter(isBalanceOperation);
};

/**
 * Filter token transactions (exclude balance operations)
 */
export const filterTokenTransactions = (transactions: Transaction[]): Transaction[] => {
  return transactions.filter(tx => !isBalanceOperation(tx));
};

